import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { Check, X as XIcon, Clock, Users, Briefcase } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { api } from "@/lib/api";
import { toast } from "sonner";

const STATUS_META = {
  interested: { label: "Interested", cls: "bg-emerald-100 text-emerald-700 border-emerald-200", icon: Check },
  reject: { label: "Rejected", cls: "bg-rose-100 text-rose-700 border-rose-200", icon: XIcon },
  hold: { label: "On Hold", cls: "bg-amber-100 text-amber-700 border-amber-200", icon: Clock },
  pending: { label: "Awaiting review", cls: "bg-slate-100 text-slate-600 border-slate-200", icon: Clock },
};

export default function ShortlistPortal() {
  const { token } = useParams();
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);

  const load = () => api.get(`/shortlist/${token}`).then((r) => setData(r.data)).catch(() => setError("Shortlist not found or link expired"));
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [token]);

  const submitFeedback = async (application_id, status, comment) => {
    try {
      await api.post(`/shortlist/${token}/feedback`, { application_id, status, comment });
      toast.success("Thanks — feedback recorded");
      load();
    } catch { toast.error("Failed to submit"); }
  };

  if (error) return <div className="grid min-h-screen place-items-center bg-slate-50 p-6 text-center"><div><div className="font-heading text-2xl text-slate-800">{error}</div><p className="mt-2 text-slate-500">Please contact your Pharmaco Hires recruiter for a fresh link.</p></div></div>;
  if (!data) return <div className="grid min-h-screen place-items-center bg-slate-50 text-slate-500">Loading…</div>;

  return (
    <div className="min-h-screen bg-slate-50">
      <header className="border-b border-slate-200 bg-white">
        <div className="mx-auto flex max-w-6xl items-center justify-between px-6 py-4">
          <div className="font-heading text-xl font-bold">
            <span className="text-primary">Pharmaco</span><span className="text-secondary">Hires</span>
            <span className="ml-3 text-xs font-normal uppercase tracking-widest text-slate-400">Client Shortlist Portal</span>
          </div>
          <div className="text-xs text-slate-500">Shared privately · Do not forward</div>
        </div>
      </header>

      <main className="mx-auto max-w-6xl px-6 py-10">
        <div className="mb-8 rounded-xl border border-slate-200 bg-white p-6">
          <div className="text-xs font-semibold uppercase tracking-widest text-secondary">Shortlist</div>
          <h1 className="mt-1 font-heading text-3xl font-semibold text-slate-900">{data.name}</h1>
          <div className="mt-2 flex flex-wrap gap-4 text-sm text-slate-600">
            {data.client_name && <div><Users className="mr-1 inline h-3.5 w-3.5" />{data.client_name}</div>}
            {data.job_title && <div><Briefcase className="mr-1 inline h-3.5 w-3.5" />{data.job_title}</div>}
            <div>Shared {new Date(data.created_at).toLocaleDateString()}</div>
          </div>
          <p className="mt-4 text-sm text-slate-600">
            Review each candidate below and mark them <strong>Interested</strong>, <strong>On Hold</strong> or <strong>Rejected</strong>.
            Add a comment to help us calibrate the shortlist. Your recruiter will see updates in real time.
          </p>
        </div>

        <div className="grid gap-5">
          {data.candidates.map((c, i) => (
            <CandidateCard key={c.id} c={c} index={i + 1} onFeedback={submitFeedback} />
          ))}
          {data.candidates.length === 0 && <div className="rounded-xl border border-dashed border-slate-300 p-12 text-center text-slate-400">No candidates in this shortlist.</div>}
        </div>

        <div className="mt-8 rounded-xl border border-slate-200 bg-white p-4 text-center text-xs text-slate-500">
          Confidential · Powered by Pharmaco Hires · Contact your recruiter for questions
        </div>
      </main>
    </div>
  );
}

function CandidateCard({ c, index, onFeedback }) {
  const [comment, setComment] = useState(c.feedback?.comment || "");
  const currentStatus = c.feedback?.status || "pending";
  const meta = STATUS_META[currentStatus] || STATUS_META.pending;
  const MI = meta.icon;

  return (
    <div className="rounded-2xl border border-slate-200 bg-white p-6" data-testid={`portal-candidate-${c.id}`}>
      <div className="flex items-start justify-between">
        <div className="flex items-start gap-4">
          <div className="grid h-12 w-12 place-items-center rounded-lg bg-primary/10 font-heading text-lg font-bold text-primary">#{index}</div>
          <div>
            <div className="font-heading text-xl font-semibold text-slate-900">{c.candidate_name}</div>
            <div className="mt-1 text-sm text-slate-600">
              {c.current_company ? `@ ${c.current_company}` : ""}
              {c.experience_years != null && <span> · {c.experience_years} yrs experience</span>}
              {c.location && <span> · {c.location}</span>}
              {c.notice_period && <span> · {c.notice_period} notice</span>}
            </div>
            {(c.tags || []).length > 0 && (
              <div className="mt-3 flex flex-wrap gap-1">
                {c.tags.map((t) => <Badge key={t} className="bg-secondary/15 text-secondary">{t}</Badge>)}
              </div>
            )}
            {c.cover_letter && (
              <div className="mt-4 max-w-2xl rounded-lg bg-slate-50 p-3 text-sm text-slate-700">
                <div className="text-xs font-semibold uppercase tracking-widest text-slate-500">Recruiter&apos;s note</div>
                <p className="mt-1 line-clamp-4">{c.cover_letter}</p>
              </div>
            )}
          </div>
        </div>
        <Badge className={`gap-1 border ${meta.cls}`}><MI className="h-3 w-3" />{meta.label}</Badge>
      </div>

      <div className="mt-5 border-t border-slate-100 pt-4">
        <Textarea placeholder="Add your feedback (optional)…" value={comment} onChange={(e) => setComment(e.target.value)} rows={2} data-testid={`portal-comment-${c.id}`} />
        <div className="mt-3 flex flex-wrap gap-2">
          <Button size="sm" className="bg-emerald-600 hover:bg-emerald-700" onClick={() => onFeedback(c.id, "interested", comment)} data-testid={`portal-interested-${c.id}`}><Check className="mr-1 h-3.5 w-3.5" />Interested</Button>
          <Button size="sm" variant="outline" className="border-amber-500 text-amber-700 hover:bg-amber-50" onClick={() => onFeedback(c.id, "hold", comment)}><Clock className="mr-1 h-3.5 w-3.5" />Hold</Button>
          <Button size="sm" variant="outline" className="border-rose-500 text-rose-700 hover:bg-rose-50" onClick={() => onFeedback(c.id, "reject", comment)}><XIcon className="mr-1 h-3.5 w-3.5" />Reject</Button>
        </div>
      </div>
    </div>
  );
}
